import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Badge, Button, Card, DataTable, TextArea, type ColumnDef } from "@/deps/ui-library";
import { useActOnApproval, useApprovals } from "@/api/queries";
import type { ApprovalItem } from "@/api/types";
import {
  KIND_LABEL,
  documentPath,
  reviewHref,
  type ReviewFilter,
} from "@/components/ApprovalReview";
import { Dialog } from "@/components/Dialog";
import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { PermittedAction } from "@/components/PermittedAction";
import { SegmentControl } from "@/components/SegmentControl";
import { formatAmount } from "@/lib/money";
import { waitingLabel } from "@/lib/dates";

type Acting = { item: ApprovalItem; mode: "act" | "return" };

/** Every draft's number reads "Draft", so a link or a row is named by what tells them apart. */
const describe = (item: ApprovalItem) =>
  [KIND_LABEL[item.kind], item.documentNumber, item.partyName].filter(Boolean).join(" ");

export function ApprovalsQueuePage() {
  const navigate = useNavigate();
  const { data, isPending } = useApprovals();
  const [filter, setFilter] = useState<ReviewFilter>("all");
  const [acting, setActing] = useState<Acting | null>(null);
  const [reason, setReason] = useState("");
  const act = useActOnApproval();

  const start = (next: Acting) => {
    act.reset();
    setReason("");
    setActing(next);
  };

  // The dialog's confirm button used to be `onClick={() => setActing(null)}`. It closed, and the
  // document did not move — while the dialog above it explained that approving would issue the
  // document and take the next statutory number.
  const decide = (item: ApprovalItem, action: "verify" | "approve" | "return") => {
    act.mutate(
      { id: item.id, action, note: action === "return" ? reason : undefined },
      {
        onSuccess: () => {
          setActing(null);
          setReason("");
        },
      },
    );
  };

  const items = (data ?? []).filter((item) => filter === "all" || item.step === filter);
  const verify = (data ?? []).filter((i) => i.step === "Verify").length;
  const approve = (data ?? []).filter((i) => i.step === "Approve").length;

  const open = (item: ApprovalItem) =>
    void navigate({ to: documentPath(item) as never, search: { review: filter } as never });

  const columns: Array<ColumnDef<ApprovalItem>> = [
    {
      id: "document",
      header: "Document",
      cell: ({ row: { original: item } }) => (
        // A real link, so Open in new tab works. It keeps its place in the sequence there too.
        <Link
          to={documentPath(item) as never}
          search={{ review: filter } as never}
          aria-label={`Open ${describe(item)}`}
          className="num text-primary text-sm hover:underline"
        >
          {item.documentNumber}
        </Link>
      ),
    },
    {
      id: "type",
      header: "Type",
      cell: ({ row: { original: item } }) => KIND_LABEL[item.kind],
    },
    {
      // The party, or a journal's narration, which has no party of its own.
      id: "details",
      header: "Details",
      cell: ({ row: { original: item } }) => item.partyName ?? "",
    },
    {
      id: "submittedBy",
      header: "Submitted by",
      cell: ({ row: { original: item } }) => item.submittedBy,
    },
    {
      id: "waiting",
      header: "Waiting",
      cell: ({ row: { original: item } }) => waitingLabel(item.submittedAt),
    },
    {
      id: "step",
      header: "Step",
      cell: ({ row: { original: item } }) => (
        <Badge colorScheme={item.step === "Approve" ? "info" : "default"} size="sm">
          {item.step}
        </Badge>
      ),
    },
    {
      id: "amount",
      header: "Amount",
      meta: { numeric: true },
      cell: ({ row: { original: item } }) => formatAmount(item.amount),
    },
    {
      id: "actions",
      header: "",
      meta: { textAlign: "right" },
      cell: ({ row: { original: item } }) => (
        <span className="flex justify-end gap-1">
          {/* The consequence is stated before it is committed, not after — the RDS rule that
                warnings explain what will happen. */}
          <PermittedAction
            permission="accounting.approvals.act"
            variant="secondary"
            onClick={() => start({ item, mode: "act" })}
          >
            {item.actionLabel}
          </PermittedAction>
          {item.canReturn === false ? null : (
            <PermittedAction
              permission="accounting.approvals.act"
              variant="ghost"
              onClick={() => start({ item, mode: "return" })}
            >
              Return
            </PermittedAction>
          )}
        </span>
      ),
    },
  ];

  const returning = acting?.mode === "return";

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Approvals"
        subtitle={isPending ? "Loading" : `${data?.length ?? 0} waiting on you`}
        actions={
          items.length > 0 ? (
            <Button
              colorScheme="primary"
              variant="secondary"
              size="sm"
              leftIcon="ListChecks"
              onClick={() => open(items[0]!)}
            >
              Review in sequence
            </Button>
          ) : null
        }
      />

      <SegmentControl
        label="Approval step"
        value={filter}
        onChange={setFilter}
        segments={[
          { value: "all", label: "All", badge: data?.length ?? 0 },
          { value: "Verify", label: "To verify", badge: verify },
          { value: "Approve", label: "To approve", badge: approve },
        ]}
      />

      <Card size="none" className="overflow-hidden">
        {items.length === 0 && !isPending ? (
          <EmptyState
            icon="CheckCheck"
            title="Nothing waiting"
            description="Documents submitted for verification or approval appear here. An empty queue means everything has been dealt with."
          />
        ) : (
          <DataTable
            aria-label="Documents waiting"
            size="sm"
            enableSorting={false}
            columns={columns}
            data={items}
            getRowId={(item) => item.id}
            loading={isPending}
            onRowClick={(row) => open(row.original)}
            rowHref={(row) => reviewHref(row.original, filter)}
            rowLabel={(row) => `Open ${describe(row.original)}`}
            pagination={{ mode: "manual", show: false }}
            containerClassName="rounded-none border-0"
          />
        )}
      </Card>

      <Dialog
        open={acting !== null}
        onClose={() => setActing(null)}
        title={
          !acting
            ? ""
            : returning
              ? `Return this ${KIND_LABEL[acting.item.kind].toLowerCase()}?`
              : `${acting.item.actionLabel} this ${KIND_LABEL[acting.item.kind].toLowerCase()}?`
        }
        description={
          returning
            ? "They can change it and submit it again. The reason stays on the document's history."
            : acting?.item.step === "Approve"
              ? "Approving issues the document: it takes the next statutory number and can no longer be edited. A mistake after that is corrected with a credit note."
              : "Verifying passes the document to an approver. It can still be returned before it is issued."
        }
        footer={
          returning ? (
            <Button
              colorScheme="primary"
              variant="solid"
              size="sm"
              disabled={act.isPending || reason.trim() === ""}
              onClick={() => acting && decide(acting.item, "return")}
            >
              {act.isPending ? "Returning" : "Return"}
            </Button>
          ) : (
            <Button
              colorScheme="primary"
              variant="solid"
              size="sm"
              disabled={act.isPending}
              onClick={() =>
                acting && decide(acting.item, acting.item.step === "Approve" ? "approve" : "verify")
              }
            >
              {act.isPending ? "Working" : acting?.item.actionLabel}
            </Button>
          )
        }
      >
        <p className="text-fg text-sm">
          {acting?.item.submittedBy} submitted this{" "}
          {acting ? waitingLabel(acting.item.submittedAt).toLowerCase() : ""} for{" "}
          <span className="num font-medium">{formatAmount(acting?.item.amount ?? "0")}</span>.
        </p>

        {returning ? (
          <div className="mt-3">
            <TextArea
              label="Reason"
              required
              rows={2}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
        ) : null}

        {/* A refusal from the server is shown here rather than swallowed: the person pressed a
            button and is owed an answer either way. */}
        {act.isError ? (
          <p role="alert" className="text-bad-text mt-2 text-sm">
            {act.error instanceof Error ? act.error.message : "That could not be done."}
          </p>
        ) : null}
      </Dialog>
    </div>
  );
}
